import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const NavbarContainer = styled.nav<{ $scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${props => props.$scrolled
    ? 'rgba(10, 14, 39, 0.95)'
    : 'rgba(10, 14, 39, 0.8)'};
  backdrop-filter: blur(10px);
  border-bottom: 1px solid ${props => props.theme.colors.border.primary}20;
  transition: all ${props => props.theme.transitions.medium};
  box-shadow: ${props => props.$scrolled ? props.theme.shadows.medium : 'none'};
`;

const NavbarContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.medium} ${props => props.theme.spacing.large};
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing.small} ${props => props.theme.spacing.medium};
  }
`;

const LogoSection = styled.a`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.medium};
  text-decoration: none;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    transform: translateY(-2px);
  }
`;

const LogoImage = styled.img`
  height: 40px;
  width: 40px;
  filter: drop-shadow(0 0 10px ${props => props.theme.colors.primary}50);

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    height: 35px;
    width: 35px;
  }
`;

const LogoText = styled.span`
  font-size: ${props => props.theme.fontSizes.large};
  font-weight: ${props => props.theme.fontWeights.bold};
  background: linear-gradient(
    135deg,
    ${props => props.theme.colors.primary} 0%,
    ${props => props.theme.colors.neon.cyan} 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const NavLinks = styled.div<{ $mobileOpen?: boolean }>`
  display: flex;
  gap: ${props => props.theme.spacing.xlarge};
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    position: fixed;
    top: 70px;
    left: 0;
    right: 0;
    background: ${props => props.theme.colors.background.primary};
    flex-direction: column;
    padding: ${props => props.theme.spacing.large};
    gap: ${props => props.theme.spacing.large};
    border-bottom: 2px solid ${props => props.theme.colors.border.primary}20;
    display: ${props => props.$mobileOpen ? 'flex' : 'none'};
    animation: slideDown 0.3s ease-out;

    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }
`;

const NavLink = styled.a`
  color: ${props => props.theme.colors.text.primary};
  text-decoration: none;
  font-size: ${props => props.theme.fontSizes.medium};
  font-weight: ${props => props.theme.fontWeights.medium};
  transition: all ${props => props.theme.transitions.fast};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background: ${props => props.theme.colors.primary};
    transition: width ${props => props.theme.transitions.fast};
  }

  &:hover {
    color: ${props => props.theme.colors.primary};

    &::after {
      width: 100%;
    }
  }
`;

const RightSection = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.medium};
  align-items: center;
`;

const HamburgerButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: ${props => props.theme.spacing.small};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: block;
  }

  div {
    width: 25px;
    height: 3px;
    background: ${props => props.theme.colors.primary};
    margin: 5px 0;
    transition: all ${props => props.theme.transitions.fast};
    border-radius: 2px;
  }
`;

const CartButton = styled.button`
  background: transparent;
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: ${props => props.theme.spacing.small} ${props => props.theme.spacing.medium};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  position: relative;

  &:hover {
    background: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.glow};

    svg {
      fill: ${props => props.theme.colors.background.primary};
    }
  }

  svg {
    width: 24px;
    height: 24px;
    fill: ${props => props.theme.colors.primary};
    transition: fill ${props => props.theme.transitions.fast};
  }
`;

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLinkClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <NavbarContainer $scrolled={scrolled}>
      <NavbarContent>
        <LogoSection href="#home">
          <LogoImage src="/assets/images/logo.png" alt="NeuralPrompt" />
          <LogoText>NeuralPrompt</LogoText>
        </LogoSection>

        <NavLinks $mobileOpen={mobileMenuOpen}>
          <NavLink href="#experience" onClick={handleLinkClick}>Desktop</NavLink>
          <NavLink href="#features" onClick={handleLinkClick}>Features</NavLink>
          <NavLink href="#versions" onClick={handleLinkClick}>Price</NavLink>
          <NavLink href="#mobile-apps" onClick={handleLinkClick}>Mobile Apps</NavLink>
          <NavLink href="#canvasoft-gallery" onClick={handleLinkClick}>Gallery</NavLink>
          <NavLink href="#community" onClick={handleLinkClick}>Community</NavLink>
        </NavLinks>

        <RightSection>
          <HamburgerButton
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle Menu"
          >
            <div></div>
            <div></div>
            <div></div>
          </HamburgerButton>

          <CartButton aria-label="Shopping Cart">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>
            </svg>
          </CartButton>
        </RightSection>
      </NavbarContent>
    </NavbarContainer>
  );
};

export default Navbar;
